import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { api } from '../lib/api'
import type { BrandSection } from '../types'

interface BrandEditorProps {
  brand: BrandSection
  onClose: () => void
}

export function BrandEditor({ brand, onClose }: BrandEditorProps) {
  const { refresh } = useApp()
  const [draft, setDraft] = useState(() => ({
    name: brand.name,
    remark: brand.remark,
    label: brand.label,
    signName: brand.signName,
    signSubtitle: brand.signSubtitle,
  }))
  const [tags, setTags] = useState(() => [...brand.banner.split(' · '), '', '', ''].slice(0, 3))
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  const set = (key: keyof typeof draft, value: string) => setDraft((current) => ({ ...current, [key]: value }))
  const setTag = (index: number, value: string) => setTags((current) => current.map((tag, tagIndex) => tagIndex === index ? value : tag))
  const save = async () => {
    if (!draft.name.trim()) { setMessage('品牌名称不能为空'); return }
    setSaving(true); setMessage('')
    try {
      await api.brandsAdmin.update(brand.id, {
        ...draft,
        banner: tags.map((tag) => tag.trim()).filter(Boolean).join(' · '),
        sortOrder: brand.sortOrder,
        enabled: brand.enabled,
      })
      await refresh()
      onClose()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '保存失败，请稍后重试')
    } finally { setSaving(false) }
  }

  return <div className="modal-backdrop brand-editor-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <section className="brand-editor-modal" role="dialog" aria-modal="true" aria-labelledby="brand-editor-title">
      <header className="modal-head"><div><small>品牌卡片快捷修改</small><h2 id="brand-editor-title">编辑品牌介绍</h2></div><button className="icon-btn" onClick={onClose} aria-label="关闭"><X/></button></header>
      <div className="brand-editor-grid">
        <label><span>顶部小标签</span><input value={draft.label} onChange={(event) => set('label', event.target.value)} /></label>
        <label><span>品牌名称</span><input value={draft.name} onChange={(event) => set('name', event.target.value)} /></label>
        <label className="brand-editor-wide"><span>品牌介绍</span><textarea rows={3} value={draft.remark} onChange={(event) => set('remark', event.target.value)} /></label>
        {tags.map((tag, index) => <label key={index}><span>卖点 {index + 1}</span><input value={tag} onChange={(event) => setTag(index, event.target.value)} /></label>)}
        <label><span>底部品牌名</span><input value={draft.signName} onChange={(event) => set('signName', event.target.value)} /></label>
        <label><span>底部副标题</span><input value={draft.signSubtitle} onChange={(event) => set('signSubtitle', event.target.value)} /></label>
      </div>
      {message && <p className="form-message">{message}</p>}
      <footer className="brand-editor-footer"><button className="ghost-btn" onClick={onClose}>取消</button><button className="primary-btn" onClick={() => void save()} disabled={saving}>{saving ? '正在保存…' : '保存修改'}</button></footer>
    </section>
  </div>
}
